import { Component } from '@theme/component';

const SCROLL_DELTA_THRESHOLD = 12;

class StickyHeader extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #lastScrollY = 0;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {HTMLElement | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.header-section');
    this.#lastScrollY = window.scrollY;
    this.#syncHeight();
    this.#syncTopState(this.#lastScrollY);
    this.#setState(window.scrollY > 0 ? 'hidden' : 'visible');
    this.dataset.stickyReady = 'true';

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const { signal } = this.#abortController;

    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(this.#syncHeight);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = null;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    delete this.dataset.stickyReady;
    delete this.dataset.stickyState;
    this.classList.remove('at-top');
    document.documentElement.style.removeProperty('--header-height');
  }

  #handleScroll = () => {
    const currentScrollY = Math.max(window.scrollY, 0);
    const scrollDelta = currentScrollY - this.#lastScrollY;

    this.#syncTopState(currentScrollY);

    if (currentScrollY <= 0) {
      this.#setState('visible');
      this.#lastScrollY = currentScrollY;
      return;
    }

    if (this.#lastScrollY <= 0 && scrollDelta > 0) {
      this.#setState('hidden');
      this.#lastScrollY = currentScrollY;
      return;
    }

    if (Math.abs(scrollDelta) < SCROLL_DELTA_THRESHOLD) {
      return;
    }

    if (scrollDelta < 0) {
      this.#setState('visible');
    } else {
      this.#setState('hidden');
    }

    this.#lastScrollY = currentScrollY;
  };

  /** @param {'hidden' | 'visible'} state */
  #setState(state) {
    if (this.dataset.stickyState === state) return;

    this.dataset.stickyState = state;
  }

  /** @param {number} scrollY */
  #syncTopState(scrollY) {
    this.classList.toggle('at-top', scrollY <= 0);
  }

  #syncHeight = () => {
    const height = Math.ceil(this.getBoundingClientRect().height);

    if (height <= 0) return;

    document.documentElement.style.setProperty('--header-height', `${height}px`);
  };
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
